/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IHeaderViewProps, Logo } from '@msdyn365-commerce-modules/header';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node, getTelemetryAttributes, getPayloadObject, TelemetryConstant, Button, onTelemetryClick, getTelemetryObject } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
//import { Customer } from '@msdyn365-commerce/retail-proxy';
import { IHeaderProps, IHeaderResources } from '../definition-extensions/header.ext.props.autogenerated';
import * as MsDyn365 from '@msdyn365-commerce/core';

/**
 * GridSizes.
 */
export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 *
 * HeaderView component.
 * @extends {React.PureComponent<IHeaderViewProps>}
 */
export class HeaderView extends React.PureComponent<IHeaderViewProps & IHeaderProps<{}>> {
    private readonly menuNode: React.RefObject<HTMLDivElement>;

    public constructor(props: IHeaderViewProps & IHeaderProps<{}>) {

        super(props);
        this.menuNode = React.createRef();
    }

    public render(): JSX.Element | null {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const { HeaderTag, HeaderContainer, HeaderTopBarContainer, Divider, MobileMenuContainer, MobileMenuBodyContainer, MobileMenuLinksContainer
        } = this.props;

        return (
            <Module {...HeaderTag}>
                <Node {...HeaderContainer}>
                    <Node {...HeaderTopBarContainer}>
                        <div className='desktop-navicon-container' ref={this.menuNode}>
                            {this._renderReactFragment(this.props.menuBar)}
                        </div>
                        <div className='mobile-navicon-container'>
                            {this.props.navIcon}
                        </div>
                        {this.props.logo}
                        {this._renderMobileLogo()}
                        {this._renderReactFragment(this.props.search)}
                        {this.props.preferredStore}
                        {this._renderAccountBlock(this.props, false)}
                        <Node {...Divider} />
                        {this.props.cartIcon}
                        {this._renderReactFragment(this.props.siteOptions)}
                    </Node>
                    <Node {...MobileMenuContainer}>
                        <Node {...MobileMenuBodyContainer}>
                            { this.props.MobileMenuHeader }
                            {this._renderReactFragment(this.props.menuBar)}
                            <Node {...MobileMenuLinksContainer}>
                                { this.props.accountLinks ? this.props.accountLinks.map(link => link) : false }
                                { this.props.siteOptions }
                                { this.props.wishListIconMobile }
                                { this.props.signInLink }
                                { this.props.signOutLink }
                            </Node>
                        </Node>
                    </Node>
                </Node>
            </Module>
        );
    }

    private _renderMobileLogo(): JSX.Element | null {
        const { config, context, typeName } = this.props;
        return (
            <Logo {
                ...{ link: config.logoLink,
                    image: config.mobileLogoImage,
                    className: 'ms-header__mobile-logo',
                    gridSettings: context.request.gridSettings,
                    requestContext: context.request,
                    typeName }} />
        );
    }

    /**
     * Function to render account block.
     * @param props - IHeaderViewProps.
     * @param isRenderForMobile - Boolean.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderAccountBlock = (props: IHeaderViewProps, isRenderForMobile: boolean): JSX.Element | null => {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const { AccountInfoDropdownParentContainer, AccountInfoDropdownPopoverConentContainer,
            accountInfoDropdownButton,
            signOutLink,
            signInLink,
            accountLinks
        } = props;

        if (AccountInfoDropdownParentContainer) {
            const accountClassName = classnames(AccountInfoDropdownParentContainer.className, isRenderForMobile ? 'account-mobile' : 'account-desktop');
            if (AccountInfoDropdownPopoverConentContainer) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {accountInfoDropdownButton}
                        <Node {...AccountInfoDropdownPopoverConentContainer}>
                            { accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false }
                            {signOutLink}
                        </Node>
                    </Node>
                );
            } else if (this.props.context.request.user.emailAddress) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {this._getSignOutButton(this.props.context.request.user, this.props.resources)}
                    </Node>
                );
            } else if (signInLink) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {signInLink}
                    </Node>
                );
            }
        }
        return null;
    };

    /**
     * Function to render react fragment.
     * @param items - React nodes.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderReactFragment = (items: React.ReactNode[]): JSX.Element | null => {
        return (
            <>
                {ArrayExtensions.hasElements(items) ? items.map((slot: React.ReactNode) => {
                    return (<>
                        {slot}
                    </>);
                }) : null}
            </>
        );
    };
     
         /**
          * Renders the sign out button.
          * @param customer - Customer object.
          * @param user - Request context user.
          * @param resources - Resources.
          * @returns React Node.
          */
         private _getSignOutButton(user: MsDyn365.IRequestContextUser | undefined, resources: IHeaderResources):
         React.ReactNode | undefined {
             const telemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!, this.props.friendlyName, this.props.telemetry);
             const payLoad = getPayloadObject('click', telemetryContent, TelemetryConstant.SignOut);
             const attributes = getTelemetryAttributes(telemetryContent, payLoad);

             return user?.emailAddress && (
                 <Button
                     className='ms-header__signout-guest-button'
                     title={resources.signOutLinkText}
                     href={user?.signOutUrl ?? ''}
                     aria-label={resources.signOutLinkAriaText}
                     onClick={onTelemetryClick(telemetryContent, payLoad, 'Sign out')}
                     {...attributes}
                 >
                     <span className='ms-header__signout-guest-button-text' aria-hidden>
                         {resources.signOutLinkText}
                     </span>
                 </Button>
             );
         }
}

export default HeaderView;
